import { MediaMatcher } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { UserInfoWithId, UserTableService } from './core/database/user-table.service';
import { AuthService } from './core/services/auth.service';
import { TokenService } from './core/services/token.service';
import { UserService, isRealStaffId } from './core/services/user.service';
import { SitePermissions } from './shared/enums/permissions';
// Not via the `shared/utils` barrel: see the note on the same import in `auth.service.ts`.
import { isAuthRoute } from './shared/utils/auth-route-utils';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImpersonationNoticeService } from './core/services/impersonation-notice.service';

export const APP_DATE_FORMATS = {
  parse: { dateInput: { month: 'short', year: 'numeric', day: 'numeric' } },
  display: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' },
    monthYearLabel: { year: 'numeric' },
  },
};

//This is used for google analytics.
declare let gtag: (command: string, targetId: string, config: { page_path: string }) => void;

@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  activeURL = '/';
  countUnread = 0;
  currentUser: string;
  initials: string;
  isDisplayRightSidenav = false;
  isIframe = false;
  isLoggedIn = false;
  mobileQuery: MediaQueryList;
  pageRefreshButton = false;
  private _mobileQueryListener: () => void;
  profileLoaded = false;
  isImpersonating = false;
  user?: UserInfoWithId;
  permissions: number[] = [];
  SitePermissions = SitePermissions;
  private unreadSubscription?: Subscription;
  private visibilityChangeHandler?: () => void;
  private destroy$ = new Subject<void>();

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private authService: AuthService,
    private msalBroadcastService: MsalBroadcastService,
    private tokenService: TokenService,
    private router: Router,
    public userService: UserService,
    private userTableService: UserTableService,
    public readonly impersonationNotice: ImpersonationNoticeService
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 100px)'); //Checks to see if the screen is mobile or not.
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
    this.displayRightSidenavOnlyOnCertainPages(router); //This is used to display the right sidenav only on certain pages.
    this.handleGoogleAnalytics(); //This is used to handle the google analytics when on production.
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;
    this.pageRefreshButton = false;

    this.userTableService.get(1).then((user) => {
      if (isAuthRoute(this.router.url)) {
        return;
      }

      const hasValidToken = this.tokenService.hasAccessToken() && !this.tokenService.isAccessTokenExpired();

      // A reload trusts the cached user without asking the API, so only a row naming a real staff record
      // is trusted. Anything else (a build before #1873 could cache a refusal as staff 0) is re-checked.
      const cachedUserIsReal = isRealStaffId(user?.staffId);

      if (cachedUserIsReal && hasValidToken) {
        this.authService.setUiAuthenticated(true);
      } else if (this.authService.isUserAuthenticated()) {
        // User has a valid MSAL session but token expired or local DB was cleared — recover via
        // login flow. Pass the page they are on so the silent recovery puts them back there;
        // without it every recovery lands on the homepage, which looks like the page ejected them.
        //
        // Read the page from window.location, NOT this.router.url. The router is configured
        // `initialNavigation: 'enabledNonBlocking'` (app.routing.ts), so bootstrap does not wait
        // for the first navigation — while this runs the router still reports '/' whatever the
        // address bar says, which would send every recovery to the homepage and reinstate the bug.
        const returnUrl = window.location.pathname + window.location.search + window.location.hash;

        // That same first navigation would open the page on the cached row while the re-check runs. When
        // the row names no one, park on 'redirect' so no page loads on it; recovery then navigates to
        // `returnUrl` for real once it has the person's own record.
        if (user !== undefined && !cachedUserIsReal) {
          this.router.navigate(['redirect']);
        }

        this.authService.login(returnUrl);
      } else {
        this.router.navigate(['login']);
      }
    });

    this.msalBroadcastService.msalSubject$.pipe(takeUntil(this.destroy$)).subscribe((result: EventMessage) => {
      if (result.eventType === EventType.LOGIN_SUCCESS) {
        this.authService.postLogin(result.payload as AuthenticationResult);
      }
    });

    this.setupTabResumeTokenRefresh();

    this.activatedNavLink();

    this.authService.uiAuthenticated.pipe(takeUntil(this.destroy$)).subscribe((authenticated) => {
      this.isLoggedIn = authenticated;
      this.updateUi();
    });

    this.userService.uiImpersonation.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateUi();
    });

    this.unreadSubscription = this.userService.message.subscribe((count) => {
      this.countUnread = count ?? 0;
      this.changeDetectorRef.markForCheck();
    });

    this.userService.apiErrorMessage.pipe(takeUntil(this.destroy$)).subscribe((apiError) => {
      if (apiError == 1) {
        console.error('There is an error communicating with the API!');
        this.pageRefreshButton = true;
      } else if (apiError == 0) {
        this.pageRefreshButton = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.unreadSubscription?.unsubscribe();
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  private setupTabResumeTokenRefresh(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible' && this.isLoggedIn && this.tokenService.isAccessTokenExpired()) {
        this.authService.refreshTokenSilent().subscribe({
          error: () => this.router.navigate(['login']),
        });
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  updateUi(): void {
    this.userTableService.get(1).then((user) => {
      if (typeof user !== 'undefined') {
        this.initials = user.userName.match(/\b(\w)/g).join('');
        this.isImpersonating = user.isImpersonating;
        this.isImpersonating && this.authService.isUserAuthenticated()
          ? this.authService.updatePermissions(user.staffId, this.refreshPermissions)
          : this.authService.updatePermissions(null, this.refreshPermissions);
        this.user = user;
        this.countUnread = user.countUnreadNotifications ?? 0;
        this.changeDetectorRef.markForCheck();
      }
    });
  }
  //#region Navigation bar
  activatedNavLink() {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.activeURL = e.url;
      }
    });
  }

  displayRightSidenavOnlyOnCertainPages(router) {
    router.events.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      if (e instanceof NavigationEnd) {
        if (e.url.indexOf('/learner/dashboard/') != -1) {
          this.isDisplayRightSidenav = true;
        } else {
          this.isDisplayRightSidenav = false;
        }
      }
    });
  }
  //#endregion

  //#region Google Analytics
  handleGoogleAnalytics() {
    if (document.location.hostname.search('vision.acttraining.org.uk') !== -1) {
      this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
        if (event instanceof NavigationEnd) {
          gtag('config', 'G-HCY04XXHTG', {
            page_path: event.urlAfterRedirects,
          });
        }
      });
    }
  }
  //#endregion

  //#region Page Functions
  refreshPage() {
    //OnClick for the popup when the api is down.
    window.location.reload();
  }

  onActivate(_event) {
    // Used in route....not sure what it does!
    window.scroll(0, 0);
  }

  navigateToStatusPage() {
    window.open(environment.statusPage, '_blank');
  }

  cancelImpersonation() {
    this.impersonationNotice.stopViewAs();
  }

  logout() {
    this.authService.logout();
  }

  refreshPermissions = () => {
    this.permissions = this.authService.permissions;
    this.userTableService.update(1, { permission: this.permissions });
    this.changeDetectorRef.detectChanges();
  };
  //#endregion
}
